"use client";

import { useRef } from "react";
import { Play, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function LuxuryHeroBanner() {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useGSAP(() => {
    if (videoRef.current) {
      gsap.to(videoRef.current, {
        scale: 1.05,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
    }
  }, {
    scope: containerRef
  });
  const tickerItems = ["DRIP DISTRICT", "PARIS", "SS25 COLLECTION", "PREMIUM TEXTILES", "FILE No. 01", "EST. 2025 SPIRIT"];
  return <section data-section-id="1523" id="luxuryherobanner" ref={containerRef} className="bg-background text-foreground relative min-h-screen w-full overflow-hidden py-0">
      {}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <video ref={videoRef} className="h-full w-full object-cover will-change-transform" autoPlay loop muted playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/hero_fashion_runway.webp">
          <source src="/img/hero_fashion_showcase.mp4" type="video/mp4" />
        </video>

        {}
        <div className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        backgroundSize: "220px 220px"
      }} />

        {}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to top right, var(--background) 0%, var(--background) 15%, transparent 65%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to bottom, transparent 0%, transparent 40%, hsl(45 68% 20% / 0.7) 100%)"
      }} />
      </div>

      {}
      <div className="absolute top-8 right-24 md:right-32 z-10 hidden md:block pointer-events-none" style={{
      animation: "spinSlow 80s linear infinite"
    }} aria-hidden="true">
        <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
          <circle cx="70" cy="70" r="6" fill="hsl(45 68% 47%)" />
          {Array.from({
          length: 9
        }).map((_, i) => {
          const angle = i * 360 / 18 - 90;
          const rad = angle * Math.PI / 180;
          const x1 = 70 + Math.cos(rad) * 16;
          const y1 = 70 + Math.sin(rad) * 16;
          const x2 = 70 + Math.cos(rad) * 64;
          const y2 = 70 + Math.sin(rad) * 64;
          return <line data-index={i} key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="hsl(45 68% 47%)" strokeWidth="1.5" strokeLinecap="round" />;
        })}
        </svg>
      </div>

      {}
      <div className="relative z-20 min-h-screen flex flex-col">
        <div className="flex-1 grid grid-cols-12 gap-4 md:gap-8 px-5 md:px-14 pt-32 md:pt-40 pb-20 md:pb-32">
          {}
          <div className="col-span-12 lg:col-span-8 flex flex-col justify-end">
            <div className="lg:hidden mb-6">
              <span className="inline-flex items-center gap-2 text-foreground/80" style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.75rem",
              letterSpacing: "0.18em"
            }}>
                <span className="border border-foreground/60 px-1.5 py-0.5">
                  01
                </span>
                FILE No. / DRIP DISTRICT — SS25
              </span>
            </div>

            <h1 ref={headlineRef} className="heading font-bold text-foreground transition-all duration-800" style={{
            fontSize: "clamp(3.25rem, 8.5vw, 8.5rem)",
            lineHeight: "0.92",
            letterSpacing: "-0.03em",
            transform: headlineVisible ? "translateY(0)" : "translateY(40px)",
            opacity: headlineVisible ? 1 : 0,
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              Style{" "}
              <span className="italic font-normal" style={{
              color: "var(--accent)",
              fontFamily: "var(--font-serif)"
            }}>
                redefined
              </span>{" "}
              for you
            </h1>

            <p ref={subRef} className="mt-8 text-foreground/85 max-w-[52ch] font-default transition-all duration-800" style={{
            fontSize: "clamp(1.05rem, 1.25vw, 1.25rem)",
            lineHeight: "1.55",
            transform: subVisible ? "translateY(0)" : "translateY(40px)",
            opacity: subVisible ? 1 : 0,
            transitionDelay: "100ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              Drip District stands at the intersection of avant-garde streetwear and contemporary luxury fashion. Crafted for bold individuals who command attention and redefine culture.
            </p>

            <div ref={ctaRef} className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4 transition-all duration-800" style={{
            transform: ctaVisible ? "translateY(0)" : "translateY(40px)",
            opacity: ctaVisible ? 1 : 0,
            transitionDelay: "200ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              <Button asChild size="lg" className="rounded-lg h-auto px-8 py-5 text-base font-semibold bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-300">
                <Link to="/ShopPage">
                  Shop Collection
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg h-auto px-8 py-5 text-base font-semibold bg-transparent border-[1.5px] border-foreground text-foreground hover:bg-foreground hover:text-background transition-all duration-300">
                <Link to="/BrandStoryPage">
                  <Play className="mr-1 h-4 w-4" />
                  Our Story
                </Link>
              </Button>

              <Button asChild size="lg" className="rounded-lg h-auto px-8 py-5 text-base font-semibold bg-accent text-accent-foreground border-none hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-200">
                <Link to="/SeasonalCollectionsPage">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Kinetic Trigger
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="hidden lg:flex col-span-4 relative justify-end items-center">
            {}
            <div className="absolute inset-0 -z-10 opacity-20 pointer-events-none overflow-hidden">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,var(--accent)_1px,transparent_1px)] [background-size:40px_40px] rotate-12" />
            </div>
            
            <div className="absolute right-0 top-1/2 -translate-y-1/2 origin-right whitespace-nowrap" style={{
            transform: "translateY(-50%) rotate(90deg)",
            transformOrigin: "right center"
          }}>
              <span className="inline-flex items-center gap-3 text-foreground/80" style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.8rem",
              letterSpacing: "0.18em"
            }}>
                <span className="border border-foreground/60 px-2 py-1">01</span>
                FILE No. / DRIP DISTRICT — SS25  PARIS
              </span>
            </div>
          </div>
        </div>

        {}
        <div className="relative z-20 border-t border-foreground/30 bg-background/40 backdrop-blur-sm overflow-hidden">
          <div className="flex whitespace-nowrap py-3" style={{
          animation: "tickerScroll 60s linear infinite"
        }}>
            {Array.from({
            length: 3
          }).map((_, repIdx) => <div data-index={repIdx} key={repIdx} className="flex shrink-0">
                {tickerItems.map((item, i) => <span data-index={i} key={`${repIdx}-${i}`} className="px-6 inline-flex items-center" style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.8rem",
              letterSpacing: "0.18em",
              color: "var(--accent)"
            }}>
                    {item}
                    <span className="ml-6 text-foreground/50"></span>
                  </span>)}
              </div>)}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="spinSlow"], [style*="tickerScroll"] {
            animation: none !important;
          }
        }
      `}</style>
    </section>;
}